// Shared contract: keep identical in user, instructor and admin portals.
export const variantsOf = product => Array.isArray(product?.variants) ? product.variants : [];
export const variantLabel = variant => Object.entries(variant?.options || {}).map(([name, value]) => `${name}: ${value}`).join(' · ');
export const findVariant = (product, id) => variantsOf(product).find(v => v.id === id && v.enabled !== false) || null;
export function validateVariants(rows = []) {
  if (!Array.isArray(rows) || rows.length > 100) return 'Add no more than 100 variations.';
  const ids = new Set(),
    combinations = new Set();
  let names;
  for (const row of rows) {
    if (!row.id || ids.has(row.id)) return 'Each variation needs a unique ID.';
    ids.add(row.id);
    const entries = Object.entries(row.options || {});
    if (!entries.length || entries.length > 3 || entries.some(([k, v]) => !k.trim() || !String(v).trim() || k.length > 40 || String(v).length > 80)) return 'Give each variation 1–3 named options and a value for each.';
    const keys = entries.map(([k]) => k.trim().toLowerCase()).sort().join('|');
    if (new Set(entries.map(([k]) => k.trim().toLowerCase())).size !== entries.length) return 'Option names must be different.';
    if (names && names !== keys) return 'Use the same option names for every variation.';
    names = keys;
    const combination = entries.map(([k, v]) => [k.trim().toLowerCase(), String(v).trim().toLowerCase()]).sort(([a], [b]) => a.localeCompare(b)).map(pair => pair.join(':')).join('|');
    if (combinations.has(combination)) return 'Two variations have the same options.';
    combinations.add(combination);
    if (row.price === '' || row.price == null || !Number.isFinite(Number(row.price)) || Number(row.price) < 0) return 'Enter a valid price for every variation.';
    if (row.stock === '' || row.stock == null || !Number.isSafeInteger(Number(row.stock)) || Number(row.stock) < 0) return 'Variation stock must be a whole number of zero or more.';
    if (row.originalPrice !== '' && row.originalPrice != null && (!Number.isFinite(Number(row.originalPrice)) || Number(row.originalPrice) < Number(row.price))) return 'A list price must be at least the selling price.';
    if (!Array.isArray(row.images) || row.images.length > 8) return 'Add up to 8 images per variation.';
  }
  return '';
}
export function stockFor(product, variantId) {
  if (variantsOf(product).length) return Number(findVariant(product, variantId)?.stock || 0);
  const value = product?.stock ?? product?.inventory;
  return value == null || value === '' ? null : Math.max(0, Number(value) || 0);
}
export function reserveStockPatch(product, lines) {
  const variants = variantsOf(product);
  if (variants.length) {
    const quantities = new Map();
    for (const line of lines) {
      if (!findVariant(product, line.variantId)) throw new Error('An ordered variation is no longer available.');
      if (!Number.isSafeInteger(line.qty) || line.qty < 1) throw new Error('Invalid order quantity.');
      quantities.set(line.variantId, (quantities.get(line.variantId) || 0) + line.qty);
    }
    const next = variants.map(v => {
      const qty = quantities.get(v.id) || 0;
      if (qty > Number(v.stock)) throw new Error(`Not enough stock for ${variantLabel(v)}.`);
      return {
        ...v,
        stock: Number(v.stock) - qty
      };
    });
    const stock = next.filter(v => v.enabled !== false).reduce((sum, v) => sum + Number(v.stock || 0), 0);
    return {
      variants: next,
      stock,
      inventory: stock
    };
  }
  if (lines.some(line => line.variantId)) throw new Error('An ordered variation has been removed.');
  const stock = stockFor(product);
  if (stock == null) return {};
  const qty = lines.reduce((sum, line) => sum + line.qty, 0);
  if (stock < qty) throw new Error(`Not enough stock for ${product.title || 'this product'}.`);
  return {
    stock: stock - qty,
    inventory: stock - qty
  };
}
